import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Autocomplete from '@mui/material/Autocomplete';
import {
  TextField,
  Grid,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Checkbox,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { red } from '@mui/material/colors';
import AddIcon from '@mui/icons-material/Add';

import { FULL_API_URL } from 'utils';
import { FileUploader, Alert, TextEditor, Loader } from 'ui';
import { LinkButton, RoundButton } from 'ui/Buttons';
import { downloadService } from 'services';
import { useErrorAutocompleteStyles } from 'shared/styles/errorAutocompleteStyles/errorAutocompleteStyles';
import { addNotificationAction } from 'store/notifications/notificationsActions';

import { matchFileFormat } from '../shared/validations';
import { initializeInputs, checkDocuments, shouldBeRemoved } from '../shared/state';
import {
  getProductsProperties,
  getThirdPartyProductSources,
  getScopeProductSources,
} from '../selectors/shopSelectors';
import { createProduct } from '../services';
import { fetchShopProductPropertiesAction, fetchShopProductsAction } from '../store/shopActions';
import { ShopProduct } from './ShopProduct/ShopProduct';
import { ShopProductDocumentsModal } from './ShopProductDocumentsModal';
import { ShopProductSourceModal } from './ShopProductSourceModal';
import { ShopProductLines } from '../shared/constants/shopProductLines';
import { documentInputFields, documentTypes } from '../shared/state/documentsUploader';

const useStyle = makeStyles((theme) => ({
  input: {
    width: '100%',
  },
  button: {
    minWidth: 120,
    marginRight: 10,
  },
  finishIcon: {
    width: 150,
    height: 150,
    color: theme.palette.primary.main,
  },
  error: {
    color: red[500],
  },
  addButton: {
    height: 55,
    marginTop: 8,
    marginLeft: 10,
  },
  documents: {
    fontSize: 10,
  },
  documentsButton: {
    textTransform: 'none',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    borderRadius: 16,
    height: 32,
    marginRight: theme.spacing(1),
  },
  documentButtonToRemove: {
    borderColor: red[500],
    color: red[500],
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  description: Yup.string().required(),
  price: Yup.number().nullable(),
  activationFee: Yup.number().nullable(),
  amount: Yup.number(),
  toolType: Yup.mixed().when('productLines', {
    is: (productLines) => productLines.name === ShopProductLines.Tools,
    then: Yup.object().shape({
      id: Yup.number().required(),
      name: Yup.string().required(),
    }),
    otherwise: Yup.object().nullable(),
  }),
  duration: Yup.object().shape({
    id: Yup.number().required(),
    name: Yup.string().required(),
  }),
  categories: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required(),
      name: Yup.string().required(),
    }),
  ),
  cancelPeriod: Yup.object().shape({
    id: Yup.number().required(),
    name: Yup.string().required(),
  }),
  productLines: Yup.object().shape({
    id: Yup.number().required(),
    name: Yup.string().required(),
  }),
  regions: Yup.mixed().when('productLines', {
    is: (productLines) =>
      ![ShopProductLines.Tools, ShopProductLines.Research].includes(productLines.name),
    then: Yup.object().shape({
      id: Yup.number().required(),
      name: Yup.string().required(),
    }),
    otherwise: Yup.object().nullable(),
  }),
  segments: Yup.mixed().when('productLines', {
    is: (productLines) =>
      ![ShopProductLines.Tools, ShopProductLines.Research].includes(productLines.name),
    then: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().required(),
          name: Yup.string().required(),
        }),
      )
      .required(),
    otherwise: Yup.array().nullable(),
  }),
  sources: Yup.object().shape({
    id: Yup.number().required(),
    name: Yup.string().required(),
  }),
  industries: Yup.mixed().when('productLines', {
    is: (productLines) =>
      ![ShopProductLines.Tools, ShopProductLines.Research].includes(productLines.name),
    then: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().required(),
          name: Yup.string().required(),
        }),
      )
      .required(),
    otherwise: Yup.array().nullable(),
  }),
  isFreeTrial: Yup.bool(),
  trialAmount: Yup.number().when(['amount', 'isFreeTrial'], {
    is: (amount, isFreeTrial) => !!amount && isFreeTrial,
    then: Yup.number().required(),
    otherwise: Yup.number(),
  }),
  active: Yup.bool(),
  orderFormInput: Yup.mixed().test('fileFormat', 'Order form have unsupported format', (value) =>
    matchFileFormat(value),
  ),
  productDescriptionInput: Yup.mixed().test(
    'fileFormat',
    'Product description have unsupported format',
    (value) => matchFileFormat(value),
  ),
  termsAndConditionsInput: Yup.mixed().test(
    'fileFormat',
    'Terms & conditions have unsupported format',
    (value) => matchFileFormat(value),
  ),
});

const getInitialValues = (product, properties) =>
  product
    ? {
        name: product.name,
        description: product.description,
        price: product.pricing && product.pricing.length > 0 ? product.pricing[0].price : undefined,
        activationFee:
          product.pricing && product.pricing.length > 0
            ? product.pricing[0].activationFee
            : undefined,
        amount: product.amount || undefined,
        toolType: properties.tools.find(({ id }) => id === product.toolType),
        duration: properties.durations.find(({ name }) => name === product.validity),
        categories: properties.categories,
        cancelPeriod: properties.cancelPeriods.find(({ name }) => name === product.cancelPeriod),
        regions: properties.regions.find(({ name }) => name === product.regions[0]),
        productLines: properties.productLines.find(({ name }) => name === product.productLines[0]),
        segments: properties.segments,
        sources: properties.sources.find(({ name }) => name === product.sources[0]),
        industries: product.industries,
        marketLabel: product.marketLabel || undefined,
        isFreeTrial: product.isFreeTrial,
        trialAmount: product.trialAmount || 0,
        active: product.active,
        ...initializeInputs,
      }
    : { ...initializeInputs };

const ShopPanelProductCreator = ({ product: editedProduct }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const autocompleteErrorClasses = useErrorAutocompleteStyles();
  const dispatch = useDispatch();

  const productProperties = useSelector(getProductsProperties);
  const scopeSources = useSelector(getScopeProductSources);
  const thirdPartySources = useSelector(getThirdPartyProductSources);

  const initialValues = getInitialValues(editedProduct, productProperties.data);

  const steps = [
    editedProduct ? t('Specify product properties') : t('Edit product properties'),
    t('Preview'),
    t('Finish'),
  ];

  const [activeStep, setActiveStep] = React.useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const [product, setProduct] = useState(null);
  const [productPreview, setProductPreview] = useState(null);

  const [multiplePrice, setMultiplePrice] = useState(false);
  const [prices, setPrices] = useState([]);
  const [activationFees, setActivationFees] = useState([]);

  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  const [unitsLabelName, setUnitsLabelName] = useState('');
  const [unitsValue, setUnitsValue] = useState(null);

  const [thirdPartySource, setThirdPartySource] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);

  const [productImage, setProductImage] = useState(null);

  const handleUnitNameChange = (name) => {
    const unit = productProperties.data.units.find(
      ({ productLineName }) => productLineName === name,
    );

    setUnitsLabelName(unit ? unit.name : '');
  };

  useEffect(() => {
    dispatch(fetchShopProductPropertiesAction());
    dispatch(fetchShopProductsAction());

    if (!editedProduct) return;

    const isMultiplePrice = editedProduct.pricing && editedProduct.pricing.length > 1;

    if (!isMultiplePrice) return;

    setMultiplePrice(isMultiplePrice);
    setPrices((editedProduct.pricing ? editedProduct.pricing : []).map(({ price }) => price));
    setActivationFees(
      (editedProduct.pricing ? editedProduct.pricing : []).map(
        ({ activationFee }) => activationFee,
      ),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editedProduct || !initialValues.sources) return;

    setThirdPartySource(initialValues.sources.thirdParty);
    handleUnitNameChange(editedProduct.productLines[0]);

    if (editedProduct.units.length && !unitsValue) {
      const { name, value } = editedProduct.units[0];
      setUnitsLabelName(name);
      setUnitsValue(value);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productProperties.data]);

  const toggleDocumentsModal = () => setShowDocumentsModal(!showDocumentsModal);
  const toggleSourceModal = () => setShowSourceModal(!showSourceModal);

  const handleChangeTierPrice = (value, tierIndex) => {
    const pricesCopy = [...prices];

    pricesCopy[tierIndex] = value ? Number(value) : undefined;

    setPrices(pricesCopy);
  };

  const handleChangeTierFee = (value, tierIndex) => {
    const feesCopy = [...activationFees];

    feesCopy[tierIndex] = value ? Number(value) : undefined;

    setActivationFees(feesCopy);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getDocumentsByType = (prod, values, documentType) => {
    const document = prod ? prod.documents.find(({ type }) => type === documentType) : null;
    const toRemove = shouldBeRemoved(values, documentType);

    return !toRemove && document ? [{ name: document.name }] : [];
  };

  const getLogoUrl = () => {
    if (productImage) {
      return productImage.preview;
    }

    return editedProduct ? editedProduct.logo : null;
  };

  const handleSubmit = (values, { validateForm }) => {
    validateForm(values);

    if (!unitsValue || (!productImage && !editedProduct)) return;
    if (
      multiplePrice &&
      (prices.some((price) => typeof price === 'undefined') ||
        activationFees.some((fee) => typeof fee === 'undefined' || fee === null))
    )
      return;

    const inputDocuments = [
      ...(values.orderFormInput
        ? [{ name: values.orderFormInput.name }]
        : getDocumentsByType(editedProduct, values, documentTypes.orderForm)),
      ...(values.productDescriptionInput
        ? [{ name: values.productDescriptionInput.name }]
        : getDocumentsByType(editedProduct, values, documentTypes.productDescription)),
      ...(values.termsAndConditionsInput
        ? [{ name: values.termsAndConditionsInput.name }]
        : getDocumentsByType(editedProduct, values, documentTypes.terms)),
    ];

    setProductPreview({
      id: 0,
      products: productProperties.data.productLines.map(({ id, name }) => ({
        type: name,
        availability: values.productLines.id === id,
        upgrade: false,
      })),
      pricing: multiplePrice
        ? prices.map((tierPrice, index) => ({
            price: tierPrice,
            tier: index + 1,
            activationFee: activationFees[index],
            active: true,
          }))
        : [
            {
              tier: null,
              price: values.price,
              activationFee: values.activationFee,
              active: true,
            },
          ],
      name: values.name,
      description: values.description,
      amount: values.amount,
      toolType: values.toolType ? values.toolType.name : null,
      duration: values.duration.name,
      categories: values.categories ? values.categories.map(({ name }) => name) : [],
      cancelPeriod: values.cancelPeriod.name,
      regions: values.regions ? [values.regions.name] : [],
      productLines: [values.productLines.name],
      segments: values.segments ? values.segments.map(({ name }) => name) : [],
      sources: [values.sources.name],
      industries: values.industries ? values.industries : [],
      purchased: false,
      marketLabel: values.marketLabel,
      isFreeTrial: values.isFreeTrial,
      trialAmount: values.trialAmount,
      ...(typeof values.isFreeTrial && { trialPeriod: 14 }),
      units: [{ name: unitsLabelName, value: unitsValue }],
      documents: inputDocuments,
      logo: getLogoUrl(),
      cancellationPeriod: 30,
      validityDays: 365,
      status: '',
    });

    setProduct({
      name: values.name,
      description: values.description,
      amount: values.amount,
      toolType: values.toolType ? values.toolType.id : null,
      duration: values.duration.id,
      categories: values.categories ? values.categories.map(({ id }) => id) : [],
      cancelPeriod: values.cancelPeriod.id,
      region: values.regions ? values.regions.id : null,
      productLine: values.productLines.id,
      segments: values.segments ? values.segments.map(({ id }) => id) : [],
      source: values.sources.id,
      industries: values.industries ? values.industries.map(({ id }) => id) : [],
      isFreeTrial: values.isFreeTrial,
      trialAmount: values.trialAmount,
      active: Boolean(values.active),
      marketLabel: values.marketLabel,
      pricing: multiplePrice
        ? prices.map((tierPrice, index) => ({
            price: tierPrice,
            tier: index + 1,
            activationFee: activationFees[index],
          }))
        : [
            {
              tier: null,
              price: values.price,
              activationFee: values.activationFee,
            },
          ],
      ...(typeof values.orderFormInput !== 'undefined' && { orderForm: values.orderFormInput }),
      ...(typeof values.productDescriptionInput !== 'undefined' && {
        productDescription: values.productDescriptionInput,
      }),
      ...(typeof values.termsAndConditionsInput !== 'undefined' && {
        terms: values.termsAndConditionsInput,
      }),
    });

    handleNext();
  };

  const create = () => {
    setIsLoading(true);

    const formData = new FormData();

    // eslint-disable-next-line
    for (const field in product) {
      const value = product[field];
      if (typeof value !== 'undefined') {
        formData.append(
          field,
          ['pricing', 'industries', 'segments', 'categories'].includes(field)
            ? JSON.stringify(value)
            : value,
        );
      }
    }

    formData.append('units', JSON.stringify([{ name: unitsLabelName, value: unitsValue }]));

    if (productImage) {
      formData.append('logo', productImage, productImage.name);
    }

    if (editedProduct) {
      formData.append('id', editedProduct.id);
    }

    createProduct(formData)
      .then(() => {
        handleNext();
      })
      .catch((error) =>
        dispatch(
          addNotificationAction({
            message: error.message,
            variant: 'error',
          }),
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  const throwFetchError = () =>
    dispatch(
      addNotificationAction({
        variant: 'error',
        message: t('Failed to get document. Please try again or contact.'),
      }),
    );

  const downloadDocument = (id, name) => {
    downloadService({
      path: `/shop/file-download/${id}/`,
      name,
      onError: throwFetchError,
    });
  };

  const removeDocument = (type, setFieldValue) => {
    const inputField = documentInputFields[type];
    if (inputField) {
      setFieldValue(documentInputFields[type], null);
    }
    editedProduct.documents = editedProduct.documents
      ? editedProduct.documents.filter((document) => document.type !== type)
      : [];
  };

  return (
    <>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {editedProduct && !editedProduct.ableToEdit && (
        <Box mt={4}>
          <Alert
            message={t(
              'This product is already purchased. Therefore, only some of the fields are enabled for editing.',
            )}
          />
        </Box>
      )}
      {productProperties.isPending && (
        <Box my={4}>
          <Loader center />
        </Box>
      )}
      <Box display={activeStep === 0 && !productProperties.isPending ? 'block' : 'none'}>
        <Box mt={4} mb={2}>
          <Typography variant="h5">{t('Specify package properties:')}</Typography>
        </Box>
        <Formik
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          validateOnChange={false}
          initialValues={initialValues}
        >
          {({ handleChange, setFieldValue, values, errors }) => (
            <Form>
              <Box width="75%" margin="0 auto">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      defaultValue={initialValues && initialValues.name}
                      label={t('Name')}
                      variant="outlined"
                      error={Boolean(errors.name)}
                      onChange={handleChange}
                      className={classes.input}
                      disabled={editedProduct && !editedProduct.ableToEdit}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="amount"
                      label={t('Amount')}
                      defaultValue={initialValues && initialValues.amount}
                      type="number"
                      variant="outlined"
                      error={Boolean(errors.amount)}
                      onChange={handleChange}
                      className={classes.input}
                      disabled={editedProduct && !editedProduct.ableToEdit}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {!!productProperties.data.durations.length && (
                      <Autocomplete
                        options={productProperties.data.durations}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('duration', value);
                        }}
                        defaultValue={initialValues ? initialValues.duration : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Duration')}
                            variant="outlined"
                            classes={errors.duration ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {!!productProperties.data.cancelPeriods.length && (
                      <Autocomplete
                        options={productProperties.data.cancelPeriods}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('cancelPeriod', value);
                        }}
                        defaultValue={initialValues ? initialValues.cancelPeriod : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Cancel period')}
                            variant="outlined"
                            classes={errors.cancelPeriod ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {!!productProperties.data.categories.length && (
                      <Autocomplete
                        multiple
                        options={productProperties.data.categories}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('categories', value);
                        }}
                        defaultValue={initialValues ? initialValues.categories : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Categories')}
                            variant="outlined"
                            classes={errors.categories ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {!!productProperties.data.productLines.length && (
                      <Autocomplete
                        options={productProperties.data.productLines}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('productLines', value);
                          handleUnitNameChange(value.name);
                          setFieldValue('toolType', null);
                        }}
                        defaultValue={initialValues ? initialValues.productLines : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Product line')}
                            variant="outlined"
                            classes={errors.productLines ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  {values.productLines && values.productLines.name === 'Tools' && (
                    <Grid item xs={6}>
                      {!!productProperties.data.tools.length && (
                        <Autocomplete
                          options={productProperties.data.tools}
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={({ id }, value) => id === value.id}
                          value={values.toolType}
                          onChange={(_, value) => setFieldValue('toolType', value)}
                          defaultValue={initialValues ? initialValues.toolType : null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('Tool type')}
                              variant="outlined"
                              classes={errors.toolType ? autocompleteErrorClasses : null}
                            />
                          )}
                          disabled={editedProduct && !editedProduct.ableToEdit}
                        />
                      )}
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    {!!productProperties.data.segments.length && (
                      <Autocomplete
                        multiple
                        options={productProperties.data.segments}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('segments', value);
                        }}
                        defaultValue={initialValues ? initialValues.segments : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Segments')}
                            variant="outlined"
                            classes={errors.segments ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {!!productProperties.data.regions.length && (
                      <Autocomplete
                        options={productProperties.data.regions}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('regions', value);
                        }}
                        defaultValue={initialValues ? initialValues.regions : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Region')}
                            variant="outlined"
                            classes={errors.regions ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {!!productProperties.data.industries.length && (
                      <Autocomplete
                        multiple
                        options={productProperties.data.industries}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('industries', value);
                        }}
                        defaultValue={initialValues ? initialValues.industries : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Industry')}
                            variant="outlined"
                            classes={errors.industries ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="marketLabel"
                      defaultValue={initialValues && initialValues.marketLabel}
                      label={t('Market label')}
                      variant="outlined"
                      error={Boolean(errors.marketLabel)}
                      onChange={handleChange}
                      className={classes.input}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <Typography variant="body1" component="span">
                          {t('Third party source:')}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Checkbox
                          name="thirdParty"
                          checked={thirdPartySource}
                          onChange={(event) => setThirdPartySource(event.target.checked)}
                          disabled={editedProduct && !editedProduct.ableToEdit}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item xs={11}>
                      <Autocomplete
                        options={thirdPartySource ? thirdPartySources : scopeSources}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={({ id }, value) => id === value.id}
                        onChange={(_, value) => {
                          setFieldValue('sources', value);
                        }}
                        defaultValue={initialValues ? initialValues.sources : null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('Source')}
                            variant="outlined"
                            classes={errors.sources ? autocompleteErrorClasses : null}
                          />
                        )}
                        disabled={editedProduct && !editedProduct.ableToEdit}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <Button
                        onClick={toggleSourceModal}
                        className={classes.addButton}
                        variant="outlined"
                        color="primary"
                      >
                        <AddIcon />
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={2}>
                    <Typography variant="body1" component="span">
                      {t('Description:')}
                    </Typography>
                  </Box>
                  <Box mt={1}>
                    <TextEditor
                      placeholder={t('Description')}
                      error={Boolean(errors.description)}
                      value={values.description}
                      onChange={(value) => setFieldValue('description', value)}
                    />
                  </Box>
                </Grid>

                {unitsLabelName && (
                  <Grid item xs={12}>
                    <Box mt={2}>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {unitsLabelName}:
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Grid container alignItems="center">
                            <TextField
                              name="unitsName"
                              label={unitsLabelName}
                              variant="outlined"
                              error={!unitsValue}
                              defaultValue={editedProduct && unitsValue ? unitsValue : ''}
                              type="number"
                              onChange={(event) => {
                                setUnitsValue(event.target.value);
                              }}
                              className={classes.input}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}
                <Grid container>
                  <Grid item xs={8}>
                    <Box mt={2}>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {t('Documents:')}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Grid container alignItems="center">
                            <ShopProductDocumentsModal
                              documents={editedProduct && editedProduct.documents}
                              open={showDocumentsModal}
                              setFieldValue={setFieldValue}
                              onClose={toggleDocumentsModal}
                            />
                            <RoundButton
                              label={editedProduct ? t('Remove & Upload') : t('Upload')}
                              onClick={toggleDocumentsModal}
                              marginRight
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {checkDocuments(errors).length !== 0 && (
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item>
                            <Grid container alignItems="center">
                              <Typography
                                variant="body1"
                                component="span"
                                className={clsx(classes.documents, {
                                  [classes.error]: checkDocuments(errors).length !== 0,
                                })}
                              >
                                {checkDocuments(errors)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {editedProduct && editedProduct.documents.length !== 0 && (
                        <Box mt={2}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography variant="body1" component="span">
                                {t('Uploaded documents:')}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Grid container alignItems="center">
                                {editedProduct.documents.map((item) => (
                                  <Chip
                                    key={item.id}
                                    className={clsx(classes.documentsButton, {
                                      [classes.documentButtonToRemove]: shouldBeRemoved(
                                        values,
                                        item.type,
                                      ),
                                    })}
                                    onClick={() => downloadDocument(item.id, item.name)}
                                    onDelete={() => removeDocument(item.type, setFieldValue)}
                                    label={item.name}
                                  />
                                ))}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                    <Box mt={2}>
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {t('Free trial:')}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Checkbox
                            name="isFreeTrial"
                            defaultChecked={initialValues && initialValues.isFreeTrial}
                            onChange={(event) => setFieldValue('isFreeTrial', event.target.checked)}
                            disabled={editedProduct && !editedProduct.ableToEdit}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    {!!values.amount && values.isFreeTrial && (
                      <Box mt={2}>
                        <Grid container alignItems="center">
                          <Grid item xs={6}>
                            <TextField
                              name="trialAmount"
                              label={t('Free trial amount')}
                              defaultValue={initialValues && initialValues.trialAmount}
                              type="number"
                              variant="outlined"
                              error={Boolean(errors.trialAmount)}
                              onChange={handleChange}
                              className={classes.input}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <Box mt={2}>
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {t('Active product:')}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Checkbox
                            name="active"
                            defaultChecked={initialValues && initialValues.active}
                            onChange={(event) => setFieldValue('active', event.target.checked)}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box mt={2}>
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography variant="body1" component="span">
                            {t('Specify price for every tier:')}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Checkbox
                            checked={multiplePrice}
                            onChange={(event) => setMultiplePrice(event.target.checked)}
                            disabled={editedProduct && !editedProduct.ableToEdit}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box mt={2}>
                      {multiplePrice && productProperties.data.tiers ? (
                        productProperties.data.tiers.map((tier, index) => (
                          <Grid container alignItems="center" spacing={2} key={tier.id}>
                            <Grid item>
                              <Typography variant="body1" component="span">
                                {tier.name}
                              </Typography>
                            </Grid>
                            <Grid item>
                              <TextField
                                name="price"
                                label={t('Price')}
                                type="number"
                                variant="outlined"
                                error={
                                  multiplePrice &&
                                  (typeof prices[index] === 'undefined' ||
                                    (prices[index] === 0 && values.isFreeTrial))
                                }
                                defaultValue={multiplePrice && prices[index]}
                                onChange={(event) =>
                                  handleChangeTierPrice(event.target.value, index)
                                }
                                className={classes.input}
                                disabled={editedProduct && !editedProduct.ableToEdit}
                              />
                            </Grid>
                            <Grid item>
                              <TextField
                                name="activationFee"
                                label={t('Activation fee')}
                                type="number"
                                variant="outlined"
                                error={
                                  multiplePrice &&
                                  (typeof activationFees[index] === 'undefined' ||
                                    (prices[index] === 0 && activationFees[index] !== 0))
                                }
                                defaultValue={multiplePrice && activationFees[index]}
                                onChange={(event) => handleChangeTierFee(event.target.value, index)}
                                className={classes.input}
                                disabled={editedProduct && !editedProduct.ableToEdit}
                              />
                            </Grid>
                          </Grid>
                        ))
                      ) : (
                        <>
                          <Grid item xs={6}>
                            <TextField
                              name="price"
                              label={t('Price')}
                              type="number"
                              variant="outlined"
                              error={
                                !multiplePrice &&
                                (typeof values.price === 'undefined' ||
                                  (values.price === 0 && values.isFreeTrial))
                              }
                              {...(!multiplePrice &&
                              initialValues &&
                              typeof values.price !== 'undefined'
                                ? { defaultValue: initialValues.price }
                                : {})}
                              onChange={handleChange}
                              className={classes.input}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              name="activationFee"
                              label={t('Activation fee')}
                              type="number"
                              variant="outlined"
                              error={
                                !multiplePrice &&
                                (typeof values.activationFee === 'undefined' ||
                                  (values.price === 0 && values.activationFee !== 0))
                              }
                              {...(!multiplePrice &&
                              initialValues &&
                              typeof values.activationFee !== 'undefined'
                                ? { defaultValue: initialValues.activationFee }
                                : {})}
                              onChange={handleChange}
                              className={classes.input}
                              disabled={editedProduct && !editedProduct.ableToEdit}
                            />
                          </Grid>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box mt={2}>
                      <Typography variant="body1" component="span">
                        {t('Product image:')}
                      </Typography>
                      <Box mt={2}>
                        <FileUploader
                          fileUrl={
                            editedProduct &&
                            editedProduct.logo &&
                            `${FULL_API_URL}${editedProduct.logo}`
                          }
                          onChange={(img) => setProductImage(img[0])}
                          error={!productImage && !editedProduct}
                          maxSizeMb={3}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box mt={4}>
                <Grid container justifyContent="flex-end">
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    className={classes.button}
                  >
                    {t('Next')}
                  </Button>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
        <ShopProductSourceModal open={showSourceModal} onClose={toggleSourceModal} />
      </Box>
      {activeStep === 1 && (
        <>
          <Box mt={4}>
            <ShopProduct data={productPreview} preview />
          </Box>
          <Grid container justifyContent="flex-end">
            <Button
              onClick={handleBack}
              className={classes.button}
              color="primary"
              variant="outlined"
            >
              {t('Back')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={create}
              className={classes.button}
              disabled={isLoading}
            >
              {editedProduct ? t('Update product') : t('Create product')}
            </Button>
          </Grid>
        </>
      )}
      {activeStep === 2 && (
        <Box p={5}>
          <Grid container justifyContent="center" alignItems="center" direction="column">
            <Grid item>
              <CheckCircleOutlineIcon className={classes.finishIcon} />
            </Grid>
            <Grid item>
              <Typography variant="h5">
                {editedProduct
                  ? t('Product successfully edited')
                  : t('Product successfully created')}
              </Typography>
            </Grid>
            <Grid item>
              <Box pt={3}>
                <LinkButton
                  to="/subscriptions/panel"
                  label={t('View product list')}
                  color="primary"
                  variant="contained"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

ShopPanelProductCreator.defaultProps = {
  product: null,
};

ShopPanelProductCreator.propTypes = {
  product: PropTypes.object,
};

export { ShopPanelProductCreator };
